/**
 * Tree guide rails, shared by every hierarchical option list in BSM.
 *
 * Originally written for the Category filter in the product list sidebar, where
 * plain indentation wasn't enough: sibling branches reuse titles (there is a
 * "Bundles" under four different parents), so without connector lines the rows
 * are genuinely ambiguous. The Content Settings category picker has the same
 * tree and the same problem, so the logic and the styling live here once rather
 * than being reimplemented per screen.
 */
import React from "react";
import PropTypes from "prop-types";
import styles from "./TreeRails.module.css";

/**
 * Annotate a flat, depth-tagged list with what each row needs to draw rails:
 *   - `isLastChild` — no later sibling at the same depth under the same parent,
 *     so its elbow terminates instead of the rail continuing past it.
 *   - `ancestorContinues[i]` — for each ancestor level (1-based, index i is
 *     level i+1), whether that ancestor still has siblings further down the
 *     list. Only then does its vertical rail need to pass through this row.
 *
 * Both are derived by scanning forward for the next row at or above a given
 * depth, which is all the sibling/parent information a flattened depth-first
 * list actually carries.
 */
export function withTreeGuides(list) {
  return list.map((option, index) => {
    const depth = option.depth;

    const continuesAt = (level) => {
      for (let i = index + 1; i < list.length; i += 1) {
        if (list[i].depth <= level) return list[i].depth === level;
      }
      return false;
    };

    const ancestorContinues = [];
    for (let level = 1; level < depth; level += 1) {
      ancestorContinues.push(continuesAt(level));
    }

    return {
      ...option,
      isLastChild: !continuesAt(depth),
      ancestorContinues,
    };
  });
}

/**
 * The rail columns for one row. Renders nothing at depth 1 (a root row has no
 * ancestors to connect to), so callers can mount it unconditionally.
 */
export function TreeRails({ depth, ancestorContinues, isLastChild }) {
  if (!depth || depth <= 1) return null;
  return (
    <span className={styles.rails} aria-hidden="true">
      {(ancestorContinues || []).map((continues, level) => {
        const isElbow = level === depth - 2;
        const railClass = isElbow
          ? `${styles.rail} ${
              isLastChild ? styles.railElbowEnd : styles.railElbow
            }`
          : `${styles.rail}${continues ? ` ${styles.railThrough}` : ""}`;
        // Index is the only stable identity here: rails are positional, not
        // data-bearing.
        // eslint-disable-next-line react/no-array-index-key
        return <span className={railClass} key={level} />;
      })}
    </span>
  );
}

TreeRails.propTypes = {
  depth: PropTypes.number,
  ancestorContinues: PropTypes.arrayOf(PropTypes.bool),
  isLastChild: PropTypes.bool,
};

TreeRails.defaultProps = {
  depth: 1,
  ancestorContinues: [],
  isLastChild: false,
};

export { styles as treeRailStyles };
export default TreeRails;
